import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import heroSprings from "@/assets/towns/colorado-springs.jpg";
import {
  Smartphone,
  Zap,
  ShoppingCart,
  Search,
  Wrench,
  Palette,
  Hammer,
  Truck,
  HardHat,
  Trees,
  Home as HomeIcon,
  Building2,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from "lucide-react";
import {
  WebDesignProcess,
  WebDesignPlatforms,
  FreeMockupSection,
} from "@/components/web-design/WebDesignSections";
import { SERVICE_PRICING } from "@/lib/pricingData";
import { CASE_STUDIES } from "@/lib/caseStudiesData";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";

const pricing = SERVICE_PRICING.find((s) => s.slug === "web-design");
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({
  service: "web-design",
  city: "colorado-springs",
})}`;

const FEATURED_CASE = CASE_STUDIES.find((c) => c.slug === "hvac-colorado-springs")
  ?? CASE_STUDIES[0];

const SPRINGS_REVIEWS = CLIENT_REVIEWS.filter((r) =>
  /Colorado Springs|Monument/i.test(r.location ?? ""),
).slice(0, 6);

export const Route = createFileRoute("/colorado-springs-web-design")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
      {
        title:
          "Colorado Springs Web Design — Custom Sites That Convert (2026)",
      },
      {
        name: "description",
        content:
          "Custom Colorado Springs web design from an owner-led local team. Sub-2-second loads, mobile-first, built to convert. Free quote in 24 hrs — since 2006.",
      },
      {
        property: "og:title",
        content: "Colorado Springs Web Design — Custom Sites That Convert (2026)",
      },
      {
        property: "og:description",
        content:
          "Owner-led Colorado Springs web design. Sub-2-second mobile loads, conversion-built, websites from $1,850. Statewide reach since 2006.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/colorado-springs-web-design" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Springs Web Design — Sites That Convert",
        description:
          "Custom, mobile-first websites for Springs trades, healthcare, and pro-services.",
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coloradowebimpressions.com/colorado-springs-web-design",
      },
    ],
  }),
  component: () => (
    <ServicePage
      slug="web-design"
      serviceName="Web Design"
      pricingRoute="/pricing"
      eyebrow="Colorado Springs Web Design"
      title="Colorado Springs web design that turns visitors into booked work"
      goldWord="booked work"
      description="Owner-led web design for Colorado Springs businesses — built by a Springs team, designed for sub-2-second mobile loads, and engineered to ring the phone."
      heroImage={{ src: heroSprings, alt: "Downtown Colorado Springs skyline with Pikes Peak at golden hour" }}
      intro={{
        heading:
          "A Colorado Springs website has one job: turn a phone-search into a phone call.",
        body: [
          "Pretty doesn't pay the bills in the Springs. A Briargate driveway, a Falcon kitchen, a Fort Carson barracks — that's where buyers actually search. We design every page mobile-first with a sticky click-to-call button two thumbs from where their finger already sits, sub-2-second load times on real 4G, and conversion paths tuned to local search intent.",
          "We're headquartered in Colorado Springs and serve businesses across Colorado Springs, Castle Rock, Monument, Parker, Fountain, Pueblo, and the broader Front Range. Most engagements start in the Springs and expand into surrounding markets — but every site we ship is built to convert in whichever Colorado city your customer is searching from.",
          "Hail and freeze cycles drive emergency-search spikes for roofers, plumbers, and HVAC. Tourist season swings retail and restaurant traffic. We bake those Springs-specific patterns into every site we build.",
        ],
      }}
      benefits={[
        "Custom-designed for Colorado Springs businesses — never a template",
        "Mobile-first, sub-2-second load speeds on real 4G",
        "On-page SEO + local schema baked in from day one",
        "Click-to-call, lead forms, neighborhood landing pages",
        "Built to capture Briargate, Falcon, Monument & base-area traffic",
        "Owner-led — direct access to Chris, no junior staff handoff",
      ]}
      features={[
        {
          icon: Palette,
          title: "Custom Springs Design",
          desc: "On-brand visuals featuring your real Woodmen-corridor office, your trucks, and your team — never stock 'Colorado mountains' photos.",
        },
        {
          icon: Smartphone,
          title: "Mobile-First Build",
          desc: "Over 70% of Colorado Springs local-search traffic is on a phone. Sticky click-to-call, big tap targets, zero pinch-and-zoom.",
        },
        {
          icon: Zap,
          title: "Sub-2-Second Loads",
          desc: "Green Core Web Vitals or we don't ship. A 4-second site loses Springs buyers before they convert.",
        },
        {
          icon: Search,
          title: "SEO-Ready Foundation",
          desc: "Local schema, NAP signals, neighborhood-aware content — the on-page work that lets your Springs site rank from launch.",
        },
        {
          icon: ShoppingCart,
          title: "E-Commerce & Booking",
          desc: "WooCommerce, Shopify, or scheduling integrations for Springs retailers, restaurants, and service businesses.",
        },
        {
          icon: Wrench,
          title: "Care & Maintenance",
          desc: "Managed hosting, security, backups, and monthly content edits — handled by our Springs team, not a ticket queue.",
        },
      ]}
      frameworkHeading="The Colorado Springs Conversion Web Design Method"
      frameworkIntro="A 5-step process refined across 100+ Colorado Springs builds — anchored from our Springs HQ, proven from Briargate to Pueblo to Castle Rock."
      framework={[
        {
          number: "01",
          title: "Discovery & Strategy",
          desc: "Your Springs service area, your competitors in Briargate and Falcon, the customers you actually want more of — mapped to a real conversion plan.",
        },
        {
          number: "02",
          title: "Conversion Wireframes",
          desc: "Page-by-page wireframes built around 'call now' — not just navigation. Approved by you before any pixel is pushed.",
        },
        {
          number: "03",
          title: "Custom Design & Copy",
          desc: "On-brand visuals + persuasive Springs-aware copywriting written by humans who live in the Pikes Peak Region.",
        },
        {
          number: "04",
          title: "Build & SEO Foundation",
          desc: "Hand-built on a fast modern stack with schema, click-to-call, conversion tracking, and neighborhood pages baked in.",
        },
        {
          number: "05",
          title: "Launch & Optimize",
          desc: "Real launch QA, 30-day analytics review, and conversion tweaks based on actual Springs visitor behavior.",
        },
      ]}
      pricing={pricing}
      sectionsAfterIntro={<WebDesignProcess />}
      sectionsBeforeFramework={<WebDesignPlatforms />}
      sectionsBeforePricing={<FreeMockupSection />}
      sectionsAfterPricing={<SpringsLocalProof />}
      industries={[
        {
          icon: Wrench,
          name: "HVAC Websites",
          desc: "Lead-gen sites for Springs HVAC — sticky call buttons, emergency-service landing pages, freeze-spike ready.",
        },
        {
          icon: Truck,
          name: "Plumbing Websites",
          desc: "24/7 emergency-call capture for Springs plumbers across Briargate, Falcon, and Monument.",
        },
        {
          icon: HomeIcon,
          name: "Roofing Websites",
          desc: "Hail-storm landing pages, instant-quote forms, Pikes Peak Region trust signals.",
        },
        {
          icon: Hammer,
          name: "General Contractors",
          desc: "Trust-building portfolios + lead forms for Springs remodelers and builders.",
        },
        {
          icon: Trees,
          name: "Landscaping",
          desc: "Seasonal landing pages, gallery-rich design, and online estimate requests for Springs lawn and design crews.",
        },
        {
          icon: HardHat,
          name: "Concrete & Excavation",
          desc: "Heavy-services sites built for credibility, photo galleries, and instant lead capture.",
        },
        {
          icon: Building2,
          name: "Multi-Location Brands",
          desc: "Centralized design with individual location pages and per-neighborhood SEO across the Springs and beyond.",
        },
        {
          icon: Zap,
          name: "Electrical Websites",
          desc: "Service-page websites and emergency-electrician click-to-call UX across Colorado Springs.",
        },
      ]}
      faqs={[
        {
          q: "Are you actually based in Colorado Springs?",
          a: "Yes — we're headquartered in Colorado Springs at 3210 E Woodmen Rd STE 110. You can drive over and meet the team. Most of our staff has lived in the Springs for 10+ years.",
        },
        {
          q: "How much does a Colorado Springs website cost?",
          a: "Most Springs small-business websites we build land between $2,500 and $11,000 depending on page count, photography, and integrations. Our Starter package is $2,500, Professional is $5,000, and Enterprise/multi-location runs $10,000+. All pricing is published on our pricing page — no 'request a quote' games.",
        },
        {
          q: "How long does a Colorado Springs website take to build?",
          a: "4–8 weeks from kickoff to launch is typical. Faster if you have your content and photos ready; longer if we're shooting fresh photography of your Springs location and writing copy from scratch.",
        },
        {
          q: "Do you also serve Castle Rock, Monument, Parker, and Pueblo?",
          a: "Yes. Most Colorado Springs web-design clients we work with also serve Monument, Tri-Lakes, Falcon, Fountain, and sometimes north into Castle Rock and Parker or south into Pueblo. We're headquartered in Colorado Springs but build sites for businesses across the entire Front Range.",
        },
        {
          q: "Can you redesign my existing Colorado Springs website without losing rankings?",
          a: "Yes. Every Springs redesign starts with an SEO migration plan: 301 redirect mapping, schema preservation, content audit, and a 30-day post-launch ranking watch. We've migrated 100+ Springs sites without losing organic traffic.",
        },
        {
          q: "Do you handle hosting after launch?",
          a: "Yes. Every site we build for Springs clients ships with our managed hosting: SSL, daily backups, security monitoring, software updates, and a 99.9% uptime SLA. One predictable monthly fee.",
        },
        {
          q: "Do I own the website when it's done?",
          a: "Yes — 100%. You own the domain, the design, the code, and all the content. If you ever leave us, we hand it all over. No hostage situations, ever.",
        },
        {
          q: "Do you build e-commerce for Colorado Springs retailers?",
          a: "Yes — WooCommerce, Shopify, or custom depending on your catalog size. We've built Springs e-commerce stores for outdoor gear, custom apparel, baked goods, and CBD.",
        },
      ]}
    />
  ),
});

function SpringsLocalProof() {
  return (
    <section className="bg-section-alt py-20 lg:py-28" aria-labelledby="springs-proof-heading">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Real Colorado Springs builds
          </span>
          <h2 id="springs-proof-heading" className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
            What a Springs <span className="text-gradient-gold">conversion build</span> looks like
          </h2>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-brand-gold/40 bg-card p-8 shadow-card sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Springs case study
              </div>
              <h3 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
                {FEATURED_CASE.headline}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {FEATURED_CASE.summary}
              </p>
              {FEATURED_CASE.quote && (
                <blockquote className="mt-5 border-l-4 border-brand-gold pl-4 text-base italic text-foreground/90">
                  "{FEATURED_CASE.quote.text}"
                  <footer className="mt-2 text-sm font-semibold not-italic text-muted-foreground">
                    — {FEATURED_CASE.quote.attribution}
                  </footer>
                </blockquote>
              )}
            </div>
            <div className="rounded-xl bg-gradient-gold p-6 text-center text-brand-black">
              <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">
                Result
              </div>
              <div className="mt-2 text-3xl font-black sm:text-4xl">
                {FEATURED_CASE.heroResult}
              </div>
            </div>
          </div>
        </div>

        {SPRINGS_REVIEWS.length > 0 && (
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {SPRINGS_REVIEWS.slice(0, 4).map((r) => (
              <figure key={r.author} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <blockquote className="text-sm leading-relaxed text-foreground/90">
                  "{r.body}"
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  {r.author}
                  {r.location ? ` — ${r.location}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free Springs web-design proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="mt-4 text-sm text-muted-foreground">
            <Link to="/colorado-springs-office-woodmen-rd" className="font-semibold text-foreground underline-offset-2 hover:underline">
              Visit our Woodmen Rd office, Google profile and listings
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
